import React from "react";

import { formatNumber, trimToDecimals, useTranslation } from "utils";

import {
  BeefFootprintCalculationSummary,
  BeefFootprintSummaryMetrics,
  CropFootprintCalculationSummary,
  CropFootprintSummaryMetrics,
  DairyFootprintCalculationSummary,
  DairyFootprintSummaryMetrics,
  PigletFootprintCalculationSummary,
  PigletFootprintSummaryMetrics,
  PorkFootprintCalculationSummary,
  PorkFootprintSummaryMetrics,
} from "models";

import { Card, Flex, Icon, OutlinedCard, Tooltip, Typography } from "components";

interface SummaryTilesProps {
  footprintCalculationSummary?:
    | BeefFootprintCalculationSummary
    | CropFootprintCalculationSummary
    | DairyFootprintCalculationSummary
    | PigletFootprintCalculationSummary
    | PorkFootprintCalculationSummary;
  hidePerYield?: boolean;
  farmId?: string;
}

export const SummaryTiles: React.FC<SummaryTilesProps> = ({ footprintCalculationSummary, hidePerYield = false }) => {
  const { t } = useTranslation("footprinting");

  // Use `in` operator to check for property existence — robust against 0-value metrics
  const isBeefCard = !!footprintCalculationSummary && "perCattleCO2e" in footprintCalculationSummary.overall;
  const isDairyCard = !!footprintCalculationSummary && "perCowCO2e" in footprintCalculationSummary.overall;
  const isPorkCard = !!footprintCalculationSummary && "perPigCO2e" in footprintCalculationSummary.overall;
  const isPigletCard = !!footprintCalculationSummary && "perSowCO2e" in footprintCalculationSummary.overall;

  return (
    <Card className="container large summary-card-wrapper">
      <Flex className="gap-16" justify="space-between" gap={16}>
        <OutlinedCard dataTestId="crop-summary-tile" className="width-100">
          <Flex vertical align="center">
            <Typography variant="h1">
              {footprintCalculationSummary ? formatNumber({ number: trimToDecimals(footprintCalculationSummary.overall.totalCO2e) }) : "N/A"} kg
            </Typography>
            <Tooltip title={t("resultsPage.summaryTiles.co2eInTotalTooltip")}>
              <Flex align="center" gap={4} className="padding-top-25">
                <Typography variant="paragraph-s" color="grey-600">
                  {t("resultsPage.summaryTiles.co2eInTotal")}
                </Typography>
                <Icon size="s" iconName="tabler/info-circle" stroke="#999" />
              </Flex>
            </Tooltip>
          </Flex>
        </OutlinedCard>

        {hidePerYield ? null : (
          <OutlinedCard dataTestId="crop-summary-tile" className="width-100">
            <Flex vertical align="center">
              <Typography variant="h1">
                {footprintCalculationSummary ? formatNumber({ number: trimToDecimals(footprintCalculationSummary.overall.perKgYieldCO2e) }) : "N/A"} kg
              </Typography>
              <Tooltip
                title={t(
                  isPorkCard
                    ? "resultsPage.summaryTiles.co2ePerKgSlaughterWeightTooltip"
                    : isPigletCard
                    ? "resultsPage.summaryTiles.co2ePerKgLiveWeightTooltip"
                    : "resultsPage.summaryTiles.co2ePerKgYieldTooltip"
                )}
              >
                <Flex align="center" gap={4} className="padding-top-25">
                  <Typography variant="paragraph-s" color="grey-600">
                    {t(
                      isPorkCard
                        ? "resultsPage.summaryTiles.co2ePerKgSlaughterWeight"
                        : isPigletCard
                        ? "resultsPage.summaryTiles.co2ePerKgLiveWeight"
                        : "resultsPage.summaryTiles.co2ePerKgYield"
                    )}
                  </Typography>
                  <Icon size="s" iconName="tabler/info-circle" stroke="#999" />
                </Flex>
              </Tooltip>
            </Flex>
          </OutlinedCard>
        )}

        <OutlinedCard dataTestId="crop-summary-tile" className="width-100">
          <Flex vertical align="center">
            <Typography variant="h1">
              {footprintCalculationSummary
                ? formatNumber({
                    number: trimToDecimals(
                      isPigletCard
                        ? (footprintCalculationSummary.overall as PigletFootprintSummaryMetrics).perSowCO2e
                        : isPorkCard
                        ? (footprintCalculationSummary.overall as PorkFootprintSummaryMetrics).perKgLiveWeightCO2e
                        : isDairyCard
                        ? (footprintCalculationSummary.overall as DairyFootprintSummaryMetrics).perCowCO2e
                        : isBeefCard
                        ? (footprintCalculationSummary.overall as BeefFootprintSummaryMetrics).perCattleCO2e
                        : (footprintCalculationSummary.overall as CropFootprintSummaryMetrics).perHaCO2e ?? 0
                    ),
                  })
                : "N/A"}{" "}
              kg
            </Typography>
            <Tooltip
              title={t(
                isPigletCard
                  ? "resultsPage.summaryTiles.co2ePerSowTooltip"
                  : isPorkCard
                  ? "resultsPage.summaryTiles.co2ePerKgLiveWeightTooltip"
                  : isDairyCard
                  ? "resultsPage.summaryTiles.co2ePerCowTooltip"
                  : isBeefCard
                  ? "resultsPage.summaryTiles.co2ePerCattleTooltip"
                  : "resultsPage.summaryTiles.co2ePerHaTooltip"
              )}
            >
              <Flex align="center" gap={4} className="padding-top-25">
                <Typography variant="paragraph-s" color="grey-600">
                  {t(
                    isPigletCard
                      ? "resultsPage.summaryTiles.co2ePerSow"
                      : isPorkCard
                      ? "resultsPage.summaryTiles.co2ePerKgLiveWeight"
                      : isDairyCard
                      ? "resultsPage.summaryTiles.co2ePerCow"
                      : isBeefCard
                      ? "resultsPage.summaryTiles.co2ePerCattle"
                      : "resultsPage.summaryTiles.co2ePerHa"
                  )}
                </Typography>
                <Icon size="s" iconName="tabler/info-circle" stroke="#999" />
              </Flex>
            </Tooltip>
          </Flex>
        </OutlinedCard>
      </Flex>
    </Card>
  );
};
